import { useEffect } from 'react';

type SeoMetaInput = {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  keywords?: string[];
  robots?: string;
  openGraphType?: string;
  structuredData?: Record<string, unknown>;
};

const DEFAULT_TITLE = 'lankatutor - Find the Best Teachers in Sri Lanka';
const DEFAULT_DESCRIPTION =
  "lankatutor helps students across Sri Lanka discover trusted teachers for O/L, A/L, and online classes.";
const DEFAULT_ROBOTS = 'index,follow';
const STRUCTURED_DATA_ID = 'seo-structured-data';

type CleanupFn = () => void;

function upsertMetaTag(
  cleanupFns: CleanupFn[],
  selector: string,
  attributeName: 'name' | 'property',
  attributeValue: string,
  content: string
) {
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
  const existed = Boolean(tag);
  const previousContent = tag ? tag.getAttribute('content') : null;

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);

  cleanupFns.push(() => {
    if (!tag) return;

    if (!existed) {
      tag.remove();
      return;
    }

    if (previousContent === null) {
      tag.removeAttribute('content');
      return;
    }

    tag.setAttribute('content', previousContent);
  });
}

function upsertCanonicalLink(cleanupFns: CleanupFn[], href: string) {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  const existed = Boolean(link);
  const previousHref = link ? link.getAttribute('href') : null;

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);

  cleanupFns.push(() => {
    if (!link) return;

    if (!existed) {
      link.remove();
      return;
    }

    if (previousHref === null) {
      link.removeAttribute('href');
      return;
    }

    link.setAttribute('href', previousHref);
  });
}

function upsertStructuredData(cleanupFns: CleanupFn[], structuredData?: Record<string, unknown>) {
  const existingScript = document.getElementById(STRUCTURED_DATA_ID) as HTMLScriptElement | null;
  const existed = Boolean(existingScript);
  const previousContent = existingScript?.textContent ?? null;

  if (!structuredData) {
    cleanupFns.push(() => {
      if (existingScript && existed && previousContent !== null) {
        existingScript.textContent = previousContent;
      }
    });
    return;
  }

  const script = existingScript ?? document.createElement('script');
  script.id = STRUCTURED_DATA_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);

  if (!existingScript) {
    document.head.appendChild(script);
  }

  cleanupFns.push(() => {
    if (!existed) {
      script.remove();
      return;
    }

    script.textContent = previousContent;
  });
}

export default function useSeoMeta({
  title,
  description,
  canonicalPath,
  image,
  keywords = [],
  robots = DEFAULT_ROBOTS,
  openGraphType = 'website',
  structuredData,
}: SeoMetaInput) {
  const structuredDataKey = structuredData ? JSON.stringify(structuredData) : '';
  const keywordContent = keywords.filter(Boolean).join(', ');

  useEffect(() => {
    const cleanupFns: CleanupFn[] = [];
    const previousTitle = document.title;
    const resolvedCanonicalUrl = canonicalPath
      ? new URL(canonicalPath, window.location.origin).toString()
      : window.location.href;
    const resolvedImage = image
      ? new URL(image, window.location.origin).toString()
      : new URL('/ln.jpg', window.location.origin).toString();

    document.title = title || DEFAULT_TITLE;
    cleanupFns.push(() => {
      document.title = previousTitle || DEFAULT_TITLE;
    });

    upsertMetaTag(cleanupFns, 'meta[name="description"]', 'name', 'description', description || DEFAULT_DESCRIPTION);
    upsertMetaTag(cleanupFns, 'meta[name="robots"]', 'name', 'robots', robots);
    upsertMetaTag(cleanupFns, 'meta[name="keywords"]', 'name', 'keywords', keywordContent);
    upsertMetaTag(cleanupFns, 'meta[property="og:title"]', 'property', 'og:title', title || DEFAULT_TITLE);
    upsertMetaTag(
      cleanupFns,
      'meta[property="og:description"]',
      'property',
      'og:description',
      description || DEFAULT_DESCRIPTION
    );
    upsertMetaTag(cleanupFns, 'meta[property="og:type"]', 'property', 'og:type', openGraphType);
    upsertMetaTag(cleanupFns, 'meta[property="og:url"]', 'property', 'og:url', resolvedCanonicalUrl);
    upsertMetaTag(cleanupFns, 'meta[property="og:image"]', 'property', 'og:image', resolvedImage);
    upsertMetaTag(cleanupFns, 'meta[property="og:site_name"]', 'property', 'og:site_name', 'lankatutor');
    upsertMetaTag(
      cleanupFns,
      'meta[name="twitter:card"]',
      'name',
      'twitter:card',
      image ? 'summary_large_image' : 'summary'
    );
    upsertMetaTag(cleanupFns, 'meta[name="twitter:title"]', 'name', 'twitter:title', title || DEFAULT_TITLE);
    upsertMetaTag(
      cleanupFns,
      'meta[name="twitter:description"]',
      'name',
      'twitter:description',
      description || DEFAULT_DESCRIPTION
    );
    upsertMetaTag(cleanupFns, 'meta[name="twitter:image"]', 'name', 'twitter:image', resolvedImage);
    upsertCanonicalLink(cleanupFns, resolvedCanonicalUrl);
    upsertStructuredData(cleanupFns, structuredData);

    return () => {
      cleanupFns.reverse().forEach((cleanup) => cleanup());
    };
  }, [
    canonicalPath,
    description,
    image,
    keywordContent,
    openGraphType,
    robots,
    structuredData,
    structuredDataKey,
    title,
  ]);
}
